<template>
  <v-card>
    <v-card-title class="justify-center">
      <span class="headline">{{ title }}</span>
      <v-btn absolute right fab @click="onCloseButtonClick()" small text color="primary">
        <v-icon dark>
          mdi-close-circle
        </v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-container>
        <v-alert v-if="pesanGagal" type="error" dense text class="mb-4">
          {{ pesanGagal }}
        </v-alert>

        <v-alert v-if="isFlatRate" type="warning" dense text class="mb-4">
          Produk {{ product.code }} saat ini memakai tarif tunggal {{ product.due_year }}% per bulan.
          Menyimpan pengaturan di bawah akan mengubahnya menjadi produk berbasis usia, dan tarif tunggalnya
          berhenti dipakai.
        </v-alert>

        <v-alert
          v-for="(pesan, i) in peringatan"
          :key="`peringatan-${i}`"
          type="info"
          dense
          text
          class="mb-2"
        >
          {{ pesan }}
        </v-alert>

        <v-row>
          <v-col cols="12" md="6">
            <div class="detail-input">
              <div class="detail-input__label">
                <label for="usia_min">Batas Usia Terendah</label>
              </div>
              <v-text-field
                id="usia_min"
                v-model="input.usia_min"
                :readonly="!isAdmin"
                :loading="loading"
                dense
                suffix="Tahun"
                placeholder="17"
                class="detail-input__input-text"
                @keypress="onlyNumber"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="detail-input">
              <div class="detail-input__label">
                <label for="usia_max">Batas Usia Tertinggi</label>
              </div>
              <v-text-field
                id="usia_max"
                v-model="input.usia_max"
                :readonly="!isAdmin"
                :loading="loading"
                dense
                suffix="Tahun"
                placeholder="55"
                class="detail-input__input-text"
                @keypress="onlyNumber"
              />
            </div>
          </v-col>
        </v-row>

        <v-alert v-if="pesanBatas" type="error" dense text class="mb-2">
          {{ pesanBatas }}
        </v-alert>

        <v-row v-if="!pesanBatas">
          <v-col cols="12" md="7">
            <h4 class="mb-1">Tarif Tiap Usia</h4>
            <p class="caption mb-2">
              Satuannya persen per bulan, sama seperti tarif tunggal produk lain. Kolom per mil hanya penolong
              untuk mencocokkan dengan ketentuan tarif yang biasanya ditulis per mil per tahun. Seluruh
              {{ daftarUsia.length }} usia harus terisi.
            </p>
            <v-data-table
              :headers="resource.tarifHeader"
              :items="barisTarif"
              :items-per-page="-1"
              item-key="age"
              hide-default-footer
              dense
              height="320px"
              fixed-header
              class="elevation-1"
            >
              <template v-slot:[`item.rate`]="{ item }">
                <v-text-field
                  v-model="tarif[item.age]"
                  :readonly="!isAdmin"
                  :error="kosong(tarif[item.age])"
                  dense
                  hide-details
                  single-line
                  suffix="%"
                  class="tarif-input"
                  @keypress="onlyNumber"
                />
              </template>
              <template v-slot:[`item.permil`]="{ item }">
                <span class="caption">{{ perMil(tarif[item.age]) }}</span>
              </template>
            </v-data-table>
          </v-col>

          <v-col cols="12" md="5">
            <h4 class="mb-1">Persentase Bulan Pendek</h4>
            <p class="caption mb-2">
              Faktor ini dikalikan ke premi setahun penuh untuk sisa bulan pada tahun kontrak mana pun, bukan
              hanya kontrak yang kurang dari setahun. Bulan kedua belas selalu satu karena tahun kontrak yang
              genap menagih premi setahun penuh, jadi nilainya tidak bisa diubah.
            </p>
            <v-data-table
              :headers="resource.faktorHeader"
              :items="faktor"
              :items-per-page="-1"
              item-key="month"
              hide-default-footer
              dense
              height="320px"
              fixed-header
              class="elevation-1"
            >
              <template v-slot:[`item.factor`]="{ item }">
                <v-text-field
                  v-if="item.month !== 12"
                  v-model="item.factor"
                  :readonly="!isAdmin"
                  :error="kosong(item.factor)"
                  dense
                  hide-details
                  single-line
                  class="tarif-input"
                  @keypress="onlyNumber"
                />
                <span v-else class="caption">1</span>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="secondary" width="100" text large @click="onCloseButtonClick()">
                Kembali
              </v-btn>
              <v-btn
                v-if="isAdmin"
                color="primary"
                width="100"
                large
                :loading="saving"
                :disabled="loading"
                @click="simpanTarif()"
              >
                Simpan
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import Swal from 'sweetalert2'
import Constant from '@/const'

import ConstantMixin from '@/constantMixin'

const _ = require('lodash')
const axios = require('axios')

// Faktor bulan kedua belas dipatok satu karena Backend menolak nilai lain: tahun kontrak yang genap
// dua belas bulan dihitung dengan faktor ini, jadi nilai selain satu akan mendiskon setiap tahun
// penuh pada seluruh kepesertaan.
const BULAN_PENUH = 12

export default {
  mixins: [ConstantMixin],
  props: {
    id_product: String
  },
  data: () => ({
    title: 'Pengaturan Tarif Usia',
    loading: false,
    saving: false,
    isAdmin: false,
    isFlatRate: false,
    pesanGagal: '',
    peringatan: [],
    product: {
      code: '',
      name: '',
      due_year: 0
    },
    input: {
      usia_min: '',
      usia_max: ''
    },
    // Tarif disimpan per usia, bukan sebagai daftar berurut, supaya menggeser batas usia tidak
    // menghilangkan angka yang sudah diisi untuk usia yang masih berada di dalam rentang.
    tarif: {},
    faktor: [],
    resource: {
      tarifHeader: [
        { text: 'Usia', value: 'age', width: '70px', sortable: false },
        { text: 'Tarif (% per bulan)', value: 'rate', sortable: false },
        { text: 'Setara (‰ per tahun)', value: 'permil', width: '140px', sortable: false }
      ],
      faktorHeader: [
        { text: 'Sisa Bulan', value: 'month', width: '90px', sortable: false },
        { text: 'Faktor', value: 'factor', sortable: false }
      ]
    }
  }),
  computed: {
    daftarUsia() {
      if (this.input.usia_min === '' || this.input.usia_max === '') return []
      const min = Number(this.input.usia_min)
      const max = Number(this.input.usia_max)
      if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < min) return []
      if (max - min > 120) return []
      const hasil = []
      for (let usia = min; usia <= max; usia++) hasil.push(usia)
      return hasil
    },
    barisTarif() {
      return this.daftarUsia.map(age => ({ age }))
    },
    pesanBatas() {
      const min = Number(this.input.usia_min)
      const max = Number(this.input.usia_max)
      if (this.input.usia_min === '' || this.input.usia_max === '')
        return 'Isi batas usia terendah dan tertinggi lebih dulu.'
      if (!Number.isInteger(min) || !Number.isInteger(max)) return 'Batas usia harus bilangan bulat.'
      if (min < 0 || max > 120) return 'Batas usia harus di antara 0 dan 120 tahun.'
      if (min > max) return 'Batas usia terendah tidak boleh lebih besar dari yang tertinggi.'
      return ''
    }
  },
  watch: {
    // Vue 2 tidak mengamati key yang baru muncul pada objek, sehingga `v-model="tarif[usia]"` untuk
    // usia yang belum punya key tidak akan menampilkan apa pun yang diketik. Melebarkan batas usia
    // justru selalu memunculkan usia baru, jadi key-nya disiapkan lewat `$set` setiap kali daftar
    // usianya berubah.
    daftarUsia: {
      handler(daftar) {
        for (const usia of daftar)
          if (!Object.prototype.hasOwnProperty.call(this.tarif, usia)) this.$set(this.tarif, usia, '')
      },
      immediate: true
    }
  },
  mounted() {
    if (_.toLower(this.$store.getters.userLoggedIn.role) == 'administrator') this.isAdmin = true
    this.siapkanFaktor([])
    if (this.id_product) this.getProduct()
  },
  methods: {
    kosong(nilai) {
      return nilai === '' || nilai === null || nilai === undefined
    },
    // Ketentuan tarif biasanya ditulis per mil per tahun, sedangkan yang disimpan persen per bulan.
    // Hanya penolong tampilan; yang dikirim ke Backend tetap angka yang diisi, tanpa konversi,
    // supaya nilainya tidak bisa bergeser karena pembulatan di sini.
    perMil(rate) {
      if (this.kosong(rate)) return '-'
      const angka = Number(rate)
      if (!Number.isFinite(angka)) return '-'
      return `${Math.round(angka * BULAN_PENUH * 10 * 1e4) / 1e4} ‰`
    },
    siapkanFaktor(tersimpan) {
      const peta = new Map((tersimpan || []).map(baris => [Number(baris.month), baris.factor]))
      this.faktor = []
      for (let month = 1; month <= BULAN_PENUH; month++)
        this.faktor.push({
          month,
          factor: month === BULAN_PENUH ? 1 : peta.has(month) ? String(peta.get(month)) : ''
        })
    },
    getProduct() {
      this.loading = true
      this.pesanGagal = ''
      this.runApiCalls(() => {
        axios
          .get(Constant.apiUrl.concat('/product/', this.id_product))
          .then(response => {
            const product = _.get(response, 'data.data', {})
            this.product.code = _.get(product, 'code', '')
            this.product.name = _.get(product, 'name', '')
            this.product.due_year = _.get(product, 'due_year', 0)
            // Produk yang dibuat sebelum penanda ini ada tidak memilikinya sama sekali, dan Backend
            // memperlakukan ketiadaan nilai sebagai tarif tunggal. Pembacaannya di sini mengikuti
            // aturan yang sama supaya peringatan konversinya tidak salah muncul atau salah hilang.
            this.isFlatRate = _.get(product, 'isFlatRate', true) !== false

            const tarifTersimpan = _.get(product, 'age_tariff', [])
            const usiaTersimpan = tarifTersimpan.map(baris => Number(baris.age))
            this.input.usia_min = String(
              _.get(product, 'usia_min', usiaTersimpan.length ? Math.min(...usiaTersimpan) : '')
            )
            this.input.usia_max = String(
              _.get(product, 'usia_max', usiaTersimpan.length ? Math.max(...usiaTersimpan) : '')
            )

            const tarif = {}
            for (const baris of tarifTersimpan) tarif[Number(baris.age)] = String(baris.rate)
            // Diganti utuh, bukan disisipkan per key, supaya tarif produk yang dibuka sebelumnya
            // tidak tertinggal. Penggantian objeknya sendiri sudah reaktif.
            this.tarif = tarif

            this.siapkanFaktor(_.get(product, 'short_period_factor', []))
            this.title = `Pengaturan Tarif Usia — Produk ${this.product.code}`
            this.loading = false
          })
          .catch(error => {
            this.loading = false
            this.pesanGagal = this.bacaPesanGagal(error)
          })
      })
    },
    bacaPesanGagal(error) {
      if (_.get(error, 'response.data.pesan')) return _.get(error, 'response.data.pesan')
      if (_.get(error, 'request')) return 'Tidak dapat menyambung ke server'
      return 'Terjadi kesalahan yang tidak dikenali'
    },
    // Diperiksa di sisi ini supaya pengisi tahu baris mana yang kosong tanpa menunggu penolakan
    // Backend. Backend tetap memeriksanya sendiri; ini bukan penggantinya.
    periksaKelengkapan() {
      if (this.pesanBatas) return this.pesanBatas
      const usiaKosong = this.daftarUsia.filter(usia => this.kosong(this.tarif[usia]))
      if (usiaKosong.length) {
        const disebut = usiaKosong.slice(0, 8).join(', ')
        const sisa = usiaKosong.length > 8 ? ` dan ${usiaKosong.length - 8} usia lainnya` : ''
        return `Tarif usia ${disebut}${sisa} belum diisi.`
      }
      const bulanKosong = this.faktor.filter(baris => this.kosong(baris.factor)).map(baris => baris.month)
      if (bulanKosong.length) return `Faktor bulan ${bulanKosong.join(', ')} belum diisi.`
      return ''
    },
    simpanTarif() {
      const belumLengkap = this.periksaKelengkapan()
      if (belumLengkap) {
        this.pesanGagal = belumLengkap
        return
      }

      const payload = {
        usia_min: Number(this.input.usia_min),
        usia_max: Number(this.input.usia_max),
        age_tariff: this.daftarUsia.map(age => ({ age, rate: Number(this.tarif[age]) })),
        short_period_factor: this.faktor.map(baris => ({
          month: baris.month,
          factor: Number(baris.factor)
        }))
      }

      this.saving = true
      this.pesanGagal = ''
      this.peringatan = []
      axios
        .put(Constant.apiUrl.concat('/product/', this.id_product, '/tariff'), payload)
        .then(response => {
          this.saving = false
          if (response.status !== 200) return
          this.isFlatRate = false
          this.peringatan = _.get(response, 'data.data.peringatan', [])
          Swal.fire('Berhasil', 'Pengaturan tarif berhasil disimpan', 'success')
          this.$emit('tariff-saved')
        })
        .catch(error => {
          this.saving = false
          this.pesanGagal = this.bacaPesanGagal(error)
        })
    },
    onCloseButtonClick() {
      this.$emit('close-form')
    }
  }
}
</script>

<style lang="scss" scoped>
.tarif-input {
  max-width: 140px;
  font-size: 0.85rem;
}
</style>
